
import React, { PropsWithChildren, useEffect, useState, useCallback } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './GoogleSearchResults.css';
import Pagination from './GoogleSearchResultsPagination/GoogleSearchResultsPagination';
import { Breadcrumbs } from './Breadcrumbs/Breadcrumbs';
import { useResizeHandler } from '../../hooks/useResizeHandler';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useApiTokenContext } from '../../providers/ApiTokenProvider/ApiTokenProvider';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import useAiSearch, {
    AI_SEARCH_CITATION_REGEX,
    buildAgentSearchPayload,
    getSummaryCitationsMap,
    parseAgentSearchCachePayload,
    parseCitationNumbers,
    parseResponseMarkdown
} from '../../hooks/useAiSearch';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Container, TextLine } from '../../importCL';
import { IAgentSearchCachePayload, TGoogleSource, IIndexedGoogleSource } from '../../types/responses/GoogleAiSearchResponse';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';

const GOOGLE_AGENT_SEARCH_FLAG = 'Google-Agent-Search';
const PAGE_SIZE = 10;
const GOOGLE_AGENT_RESPONSE_CACHE_PREFIX = 'google-agent-search-response-v2';

interface ITopSearches {
    title: string;
    link: string;
    snippet: string;
}
interface IResources {
    resourceTitle?: string;
    resourcePath?: string;
    resourceIcon?: { iconAltText: string, iconPath: string }
    resourceDescription?: string;
}
interface IGoogleSearchResultsProps extends MappedComponentProperties {
    title?: string;
    topSearches?: ITopSearches[];
    noResultsMsg?: string;
    resources?: IResources[];
}

interface IGooglePageResult {
    title: string;
    totalResults: string;
    searchTerms: string;
    count: number;
    startIndex: number;
    inputEncoding: string;
    outputEncoding: string;
    safe: string;
    cx: string;
}

interface ISearchPageInfo {
    nextPage: IGooglePageResult | null;
    currentPage: IGooglePageResult | null;
    prevPage: IGooglePageResult | null;
    totalResults: number;
    results: TGoogleSource[];
}

const EMPTY_SEARCH_PAGE_RESULT: ISearchPageInfo = {
    nextPage: null,
    currentPage: null,
    prevPage: null,
    totalResults: 0,
    results: [],
};

const buildAgentResponseCacheKey = (
    pathname: string,
    query: string,
    pageStartIndex: number,
) => {
    const normalizedQuery = query.trim().toLowerCase();
    return `${GOOGLE_AGENT_RESPONSE_CACHE_PREFIX}::${pathname}::${normalizedQuery}::${pageStartIndex}`;
};

const mapSearchPageInfo = (
    startIndex: number,
    pageSize: number,
    totalResults: number,
    results: TGoogleSource[],
    buildPageInfo: (pageStartIndex: number, totalResults: number, count: number) => IGooglePageResult,
): ISearchPageInfo => {
    const safeTotalResults = Number.isFinite(totalResults) && totalResults > 0
        ? totalResults
        : results.length;
    const hasPreviousPage = startIndex > 1;
    const viewedResultCount = Math.max(0, startIndex - 1) + results.length;
    const hasNextPage = viewedResultCount < safeTotalResults;

    return {
        nextPage: hasNextPage
            ? buildPageInfo(startIndex + pageSize, safeTotalResults, results.length)
            : null,
        currentPage: buildPageInfo(startIndex, safeTotalResults, results.length),
        prevPage: hasPreviousPage
            ? buildPageInfo(Math.max(1, startIndex - pageSize), safeTotalResults, results.length)
            : null,
        totalResults: safeTotalResults,
        results,
    };
};

const hasApiTokens = (apiTokenMap: Record<string, any>): boolean => Object.keys(apiTokenMap).length > 0;

export const GoogleSearchResultsEditConfig = {
    emptyLabel: 'Google Search Results',
    isEmpty: (props: PropsWithChildren<IGoogleSearchResultsProps>) => {
        return !props || !props.title;
    },
};
const GoogleSearchResults = (
    props: PropsWithChildren<IGoogleSearchResultsProps>,
): JSX.Element => {
    const { getTokenString, apiTokenMap } = useApiTokenContext();
    const { getFeatureFlags } = useFeatureFlags();
    const { getSearchResults } = useAiSearch();
    const [currentPage, setCurrentPage] = useState(1);
    const [showResults, setShowResults] = useState(false);
    const [agentSummaryText, setAgentSummaryText] = useState('');
    const [isAgentSummaryLoading, setIsAgentSummaryLoading] = useState(false);
    const [agentSummarySources, setAgentSummarySources] = useState<IIndexedGoogleSource[]>([]);
    const [searchPageResult, setSearchPageResult] = useState<ISearchPageInfo>(EMPTY_SEARCH_PAGE_RESULT);

    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get('q');
    const startIndex = parseInt(params.get('start') ?? '1');
    const startIndextoFilter = startIndex ? `&start=${startIndex}` : '';
    const fallbackTopSearches = props.topSearches ?? [];

    const url = window.location.pathname;
    // Keep FAQ before help so FAQ routes containing "help" still resolve to FAQ search.
    const searchPages = ['glossary', 'articles', 'faqs', 'help', 'news-press', 'credit-cards', 'about-us'];

    const sectionUrls: { [key: string]: { [key: string]: string } } = {
        glossary: { url: 'creditonebank.com/articles/glossary' },
        articles: { url: 'creditonebank.com/articles' },
        help: { url: 'creditonebank.com/help' },
        faqs: { url: 'creditonebank.com/faqs' },
        'news-press': { url: 'creditonebank.com/corporate/about-us/news-press' },
        'credit-cards': { url: 'creditonebank.com/credit-cards' },
        'about-us': { url: 'creditonebank.com/corporate/about-us' },
    };

    const section = searchPages.find((page) => url.includes(page)) || '';

    const sectionSearchUrl = sectionUrls[section]?.url;
    const filterSiteSearchByUrl = sectionSearchUrl ? `&siteSearch=${sectionSearchUrl}&siteSearchFilter=i` : '';

    const showTopSearches = false;
    const hasSearchQuery = searchQuery !== null;
    const isFirstPage = startIndex <= 1;
    const isGoogleAgentSearchEnabled = Boolean(getFeatureFlags(GOOGLE_AGENT_SEARCH_FLAG));
    const shouldUseGoogleAgentSearch = Boolean(hasSearchQuery && !showTopSearches && isGoogleAgentSearchEnabled);
    const [shouldShowGoogleAgentSummary, setShouldShowGoogleAgentSummary] = useState<boolean>(shouldUseGoogleAgentSearch && isFirstPage);

    const searchResults = useCallback((element) => {
        element?.focus();
    }, []);

    const getBreadCrumbPath = (link: string) => {
        try {
            const url = new URL(link);
            return url.pathname;
        } catch (error) {
            return link;
        }
    };

    useResizeHandler('.Footer', [showResults, searchPageResult]);

    useEffect(() => {
        if (startIndex === 1) {
            setCurrentPage(1);
        } else {
            setCurrentPage(Math.ceil(startIndex / PAGE_SIZE));
        }
    }, []);

    const resetAiOverview = () => {
        setAgentSummaryText('');
        setAgentSummarySources([]);
        setIsAgentSummaryLoading(false);
    };

    const resetSearchResults = (results: TGoogleSource[] = []) => {
        setSearchPageResult({
            ...EMPTY_SEARCH_PAGE_RESULT,
            results,
        });
    };

    const showTopSearchFallback = () => {
        resetAiOverview();
        resetSearchResults(fallbackTopSearches);
    };

    const setOverview = (summaryText: string, summarySources: IIndexedGoogleSource[] = []) => {
        setAgentSummaryText(parseResponseMarkdown(summaryText));
        setAgentSummarySources(summarySources);
    };

    const buildPageInfo = (pageStartIndex: number, totalResults: number, count: number): IGooglePageResult => ({
        title: '',
        totalResults: String(totalResults),
        searchTerms: '',
        count,
        startIndex: pageStartIndex,
        inputEncoding: '',
        outputEncoding: '',
        safe: '',
        cx: '',
    });

    const readAgentResponseFromCache = (query: string, pageStartIndex: number): IAgentSearchCachePayload | null => {
        try {
            const cached = sessionStorage.getItem(
                buildAgentResponseCacheKey(window.location.pathname, query, pageStartIndex),
            );
            if (!cached) {
                return null;
            }
            return parseAgentSearchCachePayload(cached, isFirstPage);
        } catch {
            return null;
        }
    };

    const writeAgentResponseToCache = (
        query: string,
        pageStartIndex: number,
        payload: IAgentSearchCachePayload,
    ) => {
        try {
            sessionStorage.setItem(
                buildAgentResponseCacheKey(window.location.pathname, query, pageStartIndex),
                JSON.stringify(payload),
            );
        } catch {
            // Ignore and continue without cache.
        }
    };

    const renderSummaryWithInlineCitations = (summaryText: string, summarySources: IIndexedGoogleSource[]) => {
        if (!summaryText) {
            return '';
        }

        AI_SEARCH_CITATION_REGEX.lastIndex = 0;
        const citationSourceMap = getSummaryCitationsMap(summarySources);

        const renderedSummary: React.ReactNode[] = [];
        let cursor = 0;
        let match: RegExpExecArray | null;

        while ((match = AI_SEARCH_CITATION_REGEX.exec(summaryText)) !== null) {
            if (match.index > cursor) {
                renderedSummary.push(transformHTMLParserLinks(summaryText.slice(cursor, match.index)));
            }

            const citationNumbers = parseCitationNumbers(match[1]);

            if (!citationNumbers.length) {
                renderedSummary.push(match[0]);
            } else {
                renderedSummary.push(
                    <sup
                        key={`citation-${match.index}`}
                        className='GoogleSearchResults__agentSummary__inlineCitation'
                    >
                        {citationNumbers.map((citationNumber, index) => {
                            const source = citationSourceMap.get(citationNumber);
                            const citationLabel = `[${citationNumber}]`;

                            return (
                                <React.Fragment key={`${citationNumber}-${index}`}>
                                    {index > 0 && ', '}
                                    {source?.link ? (
                                        <a
                                            className='GoogleSearchResults__agentSummary__inlineCitationLink'
                                            href={source.link}
                                            aria-label={`Citation ${citationNumber}: ${source.title}`}
                                        >
                                            {citationLabel}
                                        </a>
                                    ) : (
                                        citationLabel
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </sup>,
                );
            }

            cursor = AI_SEARCH_CITATION_REGEX.lastIndex;
        }

        if (cursor < summaryText.length) {
            renderedSummary.push(transformHTMLParserLinks(summaryText.slice(cursor)));
        }

        return renderedSummary;
    };

    const applyAgentSearchResponse = (response: IAgentSearchCachePayload) => {
        setSearchPageResult(
            mapSearchPageInfo(startIndex, PAGE_SIZE, response.totalResults, response.results, buildPageInfo),
        );

        if (!isFirstPage) {
            resetAiOverview();
            return;
        }

        const summaryText = response.summaryText?.trim() ?? '';
        setOverview(summaryText, response.summarySources);
        setIsAgentSummaryLoading(false);
    };

    const fetchGoogleProgrammableSearchResults = async () => {
        const googleSearchKey = getTokenString('googleSearchKey');
        if (!googleSearchKey) {
            console.error('Error fetching search results:', new Error('Missing googleSearchKey token'));
            showTopSearchFallback();
            return;
        }

        const searchUrl = `https://www.googleapis.com/customsearch/v1?key=${googleSearchKey}&cx=32547d276691549f7&q=${searchQuery}${startIndextoFilter}${filterSiteSearchByUrl}`;

        try {
            const response = await fetch(searchUrl);
            const data = await response.json();
            const hasProgrammableResults = Array.isArray(data?.items) && data.items.length > 0;
            const results = hasProgrammableResults ? data.items : fallbackTopSearches;

            if (!hasProgrammableResults) {
                showTopSearchFallback();
                return;
            }

            setSearchPageResult({
                nextPage: data.queries?.nextPage?.[0] ?? null,
                currentPage: data.queries?.request?.[0] ?? null,
                prevPage: data.queries?.previousPage?.[0] ?? null,
                totalResults: parseInt(data.searchInformation?.totalResults) || 0,
                results,
            });
            resetAiOverview();
        } catch (err: unknown) {
            console.error('Error fetching search results:', err);
            showTopSearchFallback();
        }
    };

    const fetchGoogleAgentSearchResults = async (query: string) => {
        if (isFirstPage) {
            setIsAgentSummaryLoading(true);
        }

        try {
            const response = await getSearchResults(query, Math.max(startIndex - 1, 0));
            if (!response?.ok) {
                throw new Error('Google Agent Search request failed');
            }

            const data = await response.json();
            const payload = buildAgentSearchPayload(data);

            if (payload.results.length === 0) {
                showTopSearchFallback();
                return;
            }

            applyAgentSearchResponse(payload);
            writeAgentResponseToCache(query, startIndex, payload);
            setShouldShowGoogleAgentSummary(!payload.showNoResultsVariant);
        } finally {
            setIsAgentSummaryLoading(false);
        }
    };

    const showNoResultsSection = () => {
        return (
            <div className='mt-8'>
                <div className='GoogleSearchResults__noResultsMessage'>
                    {props.noResultsMsg ? transformHTMLParserLinks(props.noResultsMsg) : ''}
                </div>
                <div className='flex flex-col items-start'>
                    {props.resources && (
                        props.resources?.map((resource)=> 
                            <ButtonWrapper className='md:items-start md:flex md:flex-col' size="medium" href={resource?.resourcePath} variant="link">
                                <>
                                    <TextLine
                                        icon={{
                                            alt: resource.resourceIcon?.iconAltText,
                                            customSize: 25,
                                            src: resource.resourceIcon?.iconPath
                                        }}
                                        textClass="font-bold pl-2 text-nowrap"
                                    >
                                        {resource.resourceTitle ? resource.resourceTitle : ''}
                                    </TextLine>
                                    <p className='!mb-0 ml-4 md:ml-8 text-left'>
                                        {resource.resourceDescription ? transformHTMLParserLinks(resource.resourceDescription) : ''}
                                    </p>
                                </>
                            </ButtonWrapper>
                        )
                    )}
                </div>
            </div> 
        );
    };

    useEffect(() => {
        if (!hasApiTokens(apiTokenMap)) {
            return;
        }

        const loadResults = async () => {
            if (!hasSearchQuery || showTopSearches) {
                resetAiOverview();
                resetSearchResults(fallbackTopSearches);
                setShowResults(true);
                return;
            }

            const shouldTryAgentSearch = Boolean(shouldUseGoogleAgentSearch && searchQuery);
            if (shouldTryAgentSearch && searchQuery) {
                const cachedAgentResponse = readAgentResponseFromCache(searchQuery, startIndex);
                if (cachedAgentResponse) {
                    setShouldShowGoogleAgentSummary(!cachedAgentResponse.showNoResultsVariant)
                    applyAgentSearchResponse(cachedAgentResponse);
                    setShowResults(true);
                    return;
                }
            }

            if (shouldTryAgentSearch || !isFirstPage) {
                resetAiOverview();
            }

            try {
                if (shouldTryAgentSearch && searchQuery) {
                    await fetchGoogleAgentSearchResults(searchQuery);
                } else {
                    await fetchGoogleProgrammableSearchResults();
                }
            } catch (err: unknown) {
                console.error('Error fetching AI search results:', err);
                resetAiOverview();
                await fetchGoogleProgrammableSearchResults();
            } finally {
                setShowResults(true);
            }
        };

        loadResults();
    }, [apiTokenMap]);

    return (
        <>
            {showResults && (
                <Container id='google-search-results' className='!my-12 GoogleSearchResults__flex'>
                    <div className='GoogleSearchResults' id='google-search-results'>
                        <div className='GoogleSearchResults__title'>
                            <TextLine 
                                variant='author'
                                tag='h2'
                                className='justify-left mb-0'>
                                {transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}
                            </TextLine>
                            {!searchPageResult.totalResults ? (
                                <span className='GoogleSearchResults__total' tabIndex={0} ref={searchResults}>
                                    {!hasSearchQuery && showTopSearches ? (
                                        <span>Site Search not available in author mode, <strong>&apos;Top Searches&apos;</strong> will be shown</span>
                                    ) : (
                                        showNoResultsSection()
                                    )}
                                </span>
                            ) : (
                                <span className='GoogleSearchResults__total' tabIndex={0} ref={searchResults}>
                                    Showing {searchPageResult?.currentPage?.startIndex} - {' '}
                                    {searchPageResult.nextPage
                                        ? searchPageResult?.nextPage?.startIndex -1
                                        : searchPageResult.totalResults} of {searchPageResult.totalResults} results for <strong>&apos;{searchQuery}&apos;</strong>
                                </span>
                            )}
                        </div>
                        {!searchPageResult.totalResults && <p className='mb-4'><strong>Top Searches</strong></p>}
                        {shouldShowGoogleAgentSummary && (isAgentSummaryLoading || agentSummaryText) && (
                            <div className='GoogleSearchResults__agentSummary'>
                                <div className='GoogleSearchResults__agentSummary__header'>
                                    <p className='GoogleSearchResults__agentSummary__title'><strong>AI Overview</strong></p>
                                    <span className='GoogleSearchResults__agentSummary__badge'>Generated response</span>
                                </div>
                                <p className='GoogleSearchResults__agentSummary__info'>AI-generated from search results. Verify details in cited sources.</p>
                                <p className='GoogleSearchResults__agentSummary__description'>
                                    {isAgentSummaryLoading
                                        ? 'Generating response from search results...'
                                        : renderSummaryWithInlineCitations(agentSummaryText, agentSummarySources)}
                                </p>
                                {!isAgentSummaryLoading && agentSummarySources.length > 0 && (
                                    <div className='GoogleSearchResults__agentSummary__sources'>
                                        {agentSummarySources.map((source) => (
                                            <a
                                                key={`${source.citationIndex}-${source.link}`}
                                                className='GoogleSearchResults__agentSummary__sourceLink'
                                                href={source.link}
                                            >
                                                [{source.citationIndex}] {source.title}
                                            </a>
                                        ))}
                                    </div>
                                )} 
                            </div>
                        )}
                        <div className='GoogleSearchResults__results'>
                            {searchPageResult.results &&
                                searchPageResult.results.map((result: TGoogleSource) => (
                                    <div
                                        className='GoogleSearchResults__result'
                                        key={result.link}
                                    >
                                        <div>
                                            <a
                                                className='GoogleSearchResuls__title'
                                                href={result.link}
                                            >
                                                {result.title}
                                            </a>
                                        </div>
                                        <span className='GoogleSearchResults__result__description'>
                                            {transformHTMLParserLinks(result.snippet)}
                                        </span>
                                        <Breadcrumbs url={getBreadCrumbPath(result.link)} />
                                    </div>
                                ))}
                        </div>
                    </div>
                    {searchPageResult.results && searchPageResult.totalResults > 10 && (
                        <Pagination
                            pageSize={PAGE_SIZE}
                            currentPage={currentPage}
                            totalPages={Math.ceil(searchPageResult.totalResults / PAGE_SIZE)}
                        />
                    )}
                </Container>
            )}
        </>
    );
};


export default GoogleSearchResults;
